/* Numeric density controls enhance Fabric's existing slots without replacing its grids or filters. */
class RSCollectionDensity extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.results = this.closest('results-list');
    if (!this.results) return;
    this.preferences = { desktop: this.read('desktop', [4, 6, 8], 4), mobile: this.read('mobile', [2, 3], 2) };
    this.results.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rs-density-choice]');
      if (!button || !this.results.contains(button)) return;
      const viewport = button.dataset.viewport;
      const allowed = viewport === 'desktop' ? [4, 6, 8] : viewport === 'mobile' ? [2, 3] : [];
      const columns = Number(button.dataset.rsDensityChoice);
      if (!allowed.includes(columns)) return;
      this.preferences[viewport] = columns;
      try { sessionStorage.setItem(`rs-grid-density-${viewport}`, String(columns)); } catch (_) { /* Storage can be unavailable in private sessions. */ }
      this.apply();
    }, { signal: this.controller.signal });
    this.observer = new MutationObserver(() => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.apply(); });
    });
    this.observer.observe(this.results, { childList: true, subtree: true, attributes: true, attributeFilter: ['product-grid-view'] });
    this.apply();
  }
  read(viewport, allowed, fallback) {
    try {
      const value = Number(sessionStorage.getItem(`rs-grid-density-${viewport}`));
      return allowed.includes(value) ? value : fallback;
    } catch (_) { return fallback; }
  }
  apply() {
    if (!this.results?.isConnected) return;
    const grid = this.results.querySelector('.product-grid');
    this.results.dataset.rsDensity = 'active';
    if (grid) {
      if (grid.getAttribute('product-grid-view') !== 'default') grid.setAttribute('product-grid-view', 'default');
      grid.style.setProperty('--rs-grid-columns-desktop', String(this.preferences.desktop));
      grid.style.setProperty('--rs-grid-columns-mobile', String(this.preferences.mobile));
      grid.dataset.rsDensity = 'active';
    }
    this.results.querySelectorAll('.column-options-wrapper').forEach((slot) => {
      const mobile = slot.querySelector('input[name="grid-mobile"]');
      const desktop = slot.querySelector('input[name="grid"]');
      if (!mobile && !desktop) return;
      const viewport = mobile ? 'mobile' : 'desktop';
      slot.classList.add('rs-density-slot');
      let group = slot.querySelector('.rs-density-buttons');
      if (!group) {
        group = document.createElement('div');
        group.className = 'rs-density-buttons';
        group.setAttribute('role', 'group');
        group.setAttribute('aria-label', this.dataset.accessibilityLabel || 'Satır başına ürün');
        const label = document.createElement('span');
        label.className = 'rs-density-label';
        label.textContent = this.dataset.label || 'Görünüm';
        group.append(label);
        (mobile ? [2, 3] : [4, 6, 8]).forEach((columns) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.rsDensityChoice = String(columns);
          button.dataset.viewport = viewport;
          button.textContent = String(columns);
          button.setAttribute('aria-label', `${this.dataset.accessibilityLabel || 'Satır başına ürün'}: ${columns}`);
          group.append(button);
        });
        slot.append(group);
      }
      group.querySelectorAll('button').forEach((button) => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.rsDensityChoice) === this.preferences[viewport]));
      });
    });
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('rs-collection-density')) customElements.define('rs-collection-density', RSCollectionDensity);

class RSCollectionOrder extends HTMLElement {
  connectedCallback() {
    this.results = this.closest('results-list');
    if (!this.results) return;
    this.observer?.disconnect();
    this.observer = new MutationObserver((mutations) => {
      const changed = mutations.some((mutation) => mutation.target instanceof Element && (
        mutation.target.matches('.product-grid') || [...mutation.addedNodes].some((node) =>
          node instanceof Element && (node.matches('.product-grid') || node.querySelector('.product-grid'))
        )
      ));
      if (changed) this.schedule();
    });
    this.observer.observe(this.results, { childList: true, subtree: true });
    this.schedule();
  }
  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = null; this.interleave(); });
  }
  interleave() {
    if (this.dataset.sortBy && !['manual', 'best-selling'].includes(this.dataset.sortBy)) return;
    const grid = this.results?.querySelector('.product-grid');
    if (!grid) return;
    const cards = [...grid.children].filter((card) => card.matches('[data-product-id][data-page]'));
    if (this.dataset.sortBy === 'best-selling') {
      const rank = card => {
        const value = Number(card.dataset.fablairSalesRank);
        return Number.isSafeInteger(value) && value > 0 ? value : Number.MAX_SAFE_INTEGER;
      };
      const ordered = [...cards].sort((a, b) => rank(a) - rank(b));
      this.reorder(grid, cards, ordered);
      return;
    }
    const pages = new Map();
    for (const card of cards) {
      const page = card.dataset.page;
      if (!pages.has(page)) pages.set(page, new Map());
      const products = pages.get(page);
      const productId = card.dataset.productId;
      if (!products.has(productId)) products.set(productId, []);
      products.get(productId).push(card);
    }
    const ordered = [];
    for (const products of pages.values()) {
      const groups = [...products.values()];
      const longest = Math.max(0, ...groups.map((group) => group.length));
      for (let colorIndex = 0; colorIndex < longest; colorIndex++) {
        for (const group of groups) if (group[colorIndex]) ordered.push(group[colorIndex]);
      }
    }
    this.reorder(grid, cards, ordered);
  }
  reorder(grid, cards, ordered) {
    if (ordered.every((card, index) => card === cards[index])) return;
    const fragment = document.createDocumentFragment();
    ordered.forEach((card) => fragment.append(card));
    grid.append(fragment);
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('rs-collection-order')) customElements.define('rs-collection-order', RSCollectionOrder);
